import { Archive, Check, MessageSquarePlus, Pencil, Users, X } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Text } from '../../../components/primitives';
import { FormInput } from '../../../components/primitives/FormInput';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import {
  useArchiveConversation,
  useConversations,
  useUpdateConversation,
} from '../hooks/useConversations';
import type { Conversation } from '../types';

interface ConversationDrawerPanelProps {
  readonly organizationId: string | undefined;
  readonly activeConversationId: string | null;
  readonly onClose: () => void;
  readonly onSelect: (conversationId: string) => void;
  readonly onCreate: () => void;
}

const DRAWER_WIDTH = 320;

/**
 * Pure drawer body — header, new-chat trigger, and the conversation list
 * (select / rename / archive). Kept free of `Modal` so it can be rendered and
 * exercised directly in tests (react-test-renderer cannot mount an open Modal).
 */
export function ConversationDrawerPanel({
  organizationId,
  activeConversationId,
  onClose,
  onSelect,
  onCreate,
}: ConversationDrawerPanelProps) {
  const insets = useSafeAreaInsets();
  const { data: conversations, isLoading } = useConversations(organizationId);
  const archiveMutation = useArchiveConversation(organizationId);
  const updateMutation = useUpdateConversation(organizationId);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleArchive = (id: string) => {
    archiveMutation.mutate(id);
  };

  const handleStartRename = (conv: Conversation) => {
    setEditingId(conv.id);
    setEditValue(conv.title);
  };

  const handleCancelRename = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleSaveRename = () => {
    const trimmed = editValue.trim();
    if (!trimmed || !editingId) return;
    updateMutation.mutate({ id: editingId, input: { title: trimmed } });
    setEditingId(null);
    setEditValue('');
  };

  return (
    <View style={[styles.drawer, { paddingTop: insets.top + tokens.spacing.lg }]}>
      <View style={styles.header}>
        <Text variant="bodySmall" color={colors.text.secondary} style={styles.headerLabel}>
          Conversations
        </Text>
        <Pressable
          onPress={onClose}
          hitSlop={12}
          accessibilityRole="button"
          accessibilityLabel="Close conversations drawer"
        >
          <X size={20} color={colors.text.tertiary} strokeWidth={2} />
        </Pressable>
      </View>

      <Pressable
        onPress={() => {
          onCreate();
          onClose();
        }}
        style={({ pressed }) => [styles.newButton, pressed && styles.itemPressed]}
        accessibilityRole="button"
        accessibilityLabel="Start a new conversation"
      >
        <MessageSquarePlus size={16} color={colors.primary.neon} strokeWidth={2} />
        <Text variant="body" color={colors.text.primary} style={styles.newButtonText}>
          New chat
        </Text>
      </Pressable>

      <ScrollView
        style={styles.list}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
      >
        {isLoading && (
          <Text variant="caption" color={colors.text.tertiary} style={styles.emptyState}>
            Loading…
          </Text>
        )}
        {!isLoading && (conversations?.length ?? 0) === 0 && (
          <Text variant="caption" color={colors.text.tertiary} style={styles.emptyState}>
            No conversations yet. Start a new chat to begin.
          </Text>
        )}
        {conversations?.map((conv: Conversation) => {
          const isActive = conv.id === activeConversationId;
          const isEditing = editingId === conv.id;

          if (isEditing) {
            return (
              <View key={conv.id} style={[styles.item, styles.itemActive, styles.editRow]}>
                <FormInput
                  value={editValue}
                  onChangeText={setEditValue}
                  style={styles.editInput}
                  autoFocus
                  onSubmitEditing={handleSaveRename}
                  returnKeyType="done"
                  accessibilityLabel="Conversation name"
                />
                <Pressable
                  onPress={handleSaveRename}
                  hitSlop={10}
                  style={({ pressed }) => [styles.itemAction, pressed && styles.itemPressed]}
                  accessibilityRole="button"
                  accessibilityLabel="Save conversation name"
                >
                  <Check size={16} color={colors.primary.neon} strokeWidth={2} />
                </Pressable>
                <Pressable
                  onPress={handleCancelRename}
                  hitSlop={10}
                  style={({ pressed }) => [styles.itemAction, pressed && styles.itemPressed]}
                  accessibilityRole="button"
                  accessibilityLabel="Cancel rename"
                >
                  <X size={16} color={colors.text.tertiary} strokeWidth={2} />
                </Pressable>
              </View>
            );
          }

          // Render selection + archive as sibling Pressables instead
          // of nesting them. React Native's gesture system routes
          // taps to the outermost responder, so a nested archive
          // Pressable never fires — stopPropagation is a DOM API,
          // not RN. Two side-by-side Pressables each get their own
          // taps cleanly.
          return (
            <View key={conv.id} style={[styles.item, isActive && styles.itemActive]}>
              <Pressable
                onPress={() => {
                  onSelect(conv.id);
                  onClose();
                }}
                style={({ pressed }) => [styles.itemContent, pressed && styles.itemPressed]}
                accessibilityRole="button"
                accessibilityLabel={`Open conversation ${conv.title}`}
              >
                <Text
                  variant="body"
                  color={isActive ? colors.text.primary : colors.text.secondary}
                  numberOfLines={1}
                  style={styles.itemTitle}
                >
                  {conv.title || 'Untitled chat'}
                </Text>
                {conv.visibility === 'ORGANIZATION' && (
                  <View style={styles.visibilityBadge}>
                    <Users size={10} color={colors.text.tertiary} strokeWidth={2} />
                    <Text variant="caption" color={colors.text.tertiary} style={styles.badgeText}>
                      Shared
                    </Text>
                  </View>
                )}
              </Pressable>
              <Pressable
                onPress={() => handleStartRename(conv)}
                hitSlop={10}
                style={({ pressed }) => [styles.itemAction, pressed && styles.itemPressed]}
                accessibilityRole="button"
                accessibilityLabel={`Rename conversation ${conv.title}`}
              >
                <Pencil size={16} color={colors.text.tertiary} strokeWidth={2} />
              </Pressable>
              <Pressable
                onPress={() => handleArchive(conv.id)}
                hitSlop={10}
                style={({ pressed }) => [styles.itemAction, pressed && styles.itemPressed]}
                accessibilityRole="button"
                accessibilityLabel={`Archive conversation ${conv.title}`}
              >
                <Archive size={16} color={colors.text.tertiary} strokeWidth={2} />
              </Pressable>
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
}

interface ConversationDrawerProps {
  readonly visible: boolean;
  readonly organizationId: string | undefined;
  readonly activeConversationId: string | null;
  readonly onClose: () => void;
  readonly onSelect: (conversationId: string) => void;
  readonly onCreate: () => void;
}

export function ConversationDrawer({
  visible,
  organizationId,
  activeConversationId,
  onClose,
  onSelect,
  onCreate,
}: ConversationDrawerProps) {
  // Slide-in: drawer rests at translateX 0 when open, off-screen at -DRAWER_WIDTH when closed.
  const translateX = useRef(new Animated.Value(-DRAWER_WIDTH)).current;
  const backdropOpacity = useRef(new Animated.Value(0)).current;

  // Keep the <Modal> mounted while the close animation plays. Without
  // this, RN's Modal yanks its children the instant `visible` flips to
  // false and the slide-out is invisible — the drawer just snaps shut.
  const [mounted, setMounted] = useState(visible);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      Animated.parallel([
        Animated.timing(translateX, {
          toValue: 0,
          duration: 240,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(backdropOpacity, {
          toValue: 1,
          duration: 240,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
      ]).start();
      return;
    }

    Animated.parallel([
      Animated.timing(translateX, {
        toValue: -DRAWER_WIDTH,
        duration: 220,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(backdropOpacity, {
        toValue: 0,
        duration: 220,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start(({ finished }) => {
      // Only unmount when the close animation actually played through.
      // If the user re-opens mid-flight, finished=false and we leave
      // the Modal mounted.
      if (finished) setMounted(false);
    });
  }, [visible, translateX, backdropOpacity]);

  return (
    <Modal visible={mounted} transparent animationType="none" onRequestClose={onClose}>
      <View style={styles.root}>
        <Animated.View style={[styles.backdrop, { opacity: backdropOpacity }]}>
          <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        </Animated.View>

        <Animated.View style={{ transform: [{ translateX }] }}>
          <ConversationDrawerPanel
            organizationId={organizationId}
            activeConversationId={activeConversationId}
            onClose={onClose}
            onSelect={onSelect}
            onCreate={onCreate}
          />
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'row',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: colors.background.overlay,
  },
  drawer: {
    width: DRAWER_WIDTH,
    height: '100%',
    backgroundColor: colors.background.card,
    borderRightWidth: 1,
    borderRightColor: colors.border.subtle,
    paddingHorizontal: tokens.spacing.md,
    ...Platform.select({
      web: { boxShadow: '4px 0 20px rgba(0, 0, 0, 0.4)' as never },
      default: {
        elevation: 8,
        shadowColor: '#000',
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 0.4,
        shadowRadius: 20,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacing.md,
  },
  headerLabel: {
    fontSize: tokens.fontSize.xs,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  newButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.primary.muted,
    marginBottom: tokens.spacing.md,
  },
  newButtonText: {
    fontWeight: '600',
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingBottom: tokens.spacing.xl,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    marginBottom: tokens.spacing.xs,
  },
  itemActive: {
    backgroundColor: colors.surface.row,
    borderWidth: 1,
    borderColor: colors.border.subtle,
  },
  itemPressed: {
    opacity: 0.6,
  },
  itemContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm + 2,
  },
  itemAction: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radius.sm,
  },
  editRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    paddingLeft: tokens.spacing.md,
  },
  editInput: {
    flex: 1,
    fontSize: tokens.fontSize.base,
    color: colors.text.primary,
    paddingVertical: tokens.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.primary.muted,
  },
  itemTitle: {
    fontSize: tokens.fontSize.base,
    flexShrink: 1,
  },
  visibilityBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: tokens.spacing.xxs,
    backgroundColor: colors.background.elevated,
    borderRadius: tokens.radius.sm,
  },
  badgeText: {
    fontSize: 9,
    fontWeight: '600',
  },
  emptyState: {
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.lg,
    textAlign: 'center',
  },
});
